import { FC, ReactNode } from "react";
import {
  Input,
  InputGroup,
  InputGroupProps,
  InputLeftElement,
  InputRightElement,
} from "@chakra-ui/react";
import { FiSearch } from "react-icons/fi";
import { theme } from "@ovou-inc/ovou-ui";

interface SearchFieldProps {
  onChange: (value: string) => void;
  value?: string;
  placeholder?: string;
  width?: InputGroupProps["w"];
  ariaLabel?: string;
  // Optional trailing content (e.g. a loading spinner while a search resolves).
  rightElement?: ReactNode;
}

/**
 * The on-brand search field used across the app (templates list, member
 * lists): a Chakra input with a leading magnifier and an optional trailing
 * element. Replaces the per-screen copies of this exact markup. Distinct from
 * the legacy Tailwind `SearchInput`, which self-debounces and has a different
 * look.
 */
export const SearchField: FC<SearchFieldProps> = ({
  onChange,
  value,
  placeholder = "Search",
  width = "100%",
  ariaLabel,
  rightElement,
}) => (
  <InputGroup w={width}>
    <InputLeftElement pointerEvents="none" h="100%">
      <FiSearch size={20} color={theme.palette.ui.greys.grey2} />
    </InputLeftElement>
    <Input
      aria-label={ariaLabel}
      value={value}
      placeholder={placeholder}
      bg={theme.palette.brand.primary.white}
      border="1px solid"
      borderColor={theme.palette.ui.greys.grey3}
      borderRadius="10px"
      fontSize="16px"
      fontFamily="Inter"
      fontWeight={400}
      color={theme.palette.brand.primary.black}
      h="40px"
      pl="36px"
      pr="12px"
      _placeholder={{ color: theme.palette.ui.greys.grey1 }}
      _hover={{ borderColor: theme.palette.ui.greys.grey2 }}
      _focus={{ borderColor: theme.palette.ui.greys.grey2, boxShadow: "none" }}
      onChange={e => onChange(e.target.value)}
    />
    {rightElement && (
      <InputRightElement h="100%">{rightElement}</InputRightElement>
    )}
  </InputGroup>
);
